'use client';

import { GWPCountdown } from '@/components/gift-with-purchase/gwp-countdown';
import { Button } from '@/components/ui/button';
import type { CartProps } from '@/features/cart/components/cart';
import { CartSummary } from '@/features/cart/components/summary';
import { siteConfig } from '@/site-config';
import {
  CartEmptyItems,
  CartItem,
  CartShared,
} from '@veranda/shared/cart/components';
import { useCartSheet, useCartViewTracking } from '@veranda/shared/cart/hooks';
import { CustomLink } from '@veranda/shared/components/custom-link';
import { NavbarButton } from '@veranda/shared/layout/navbar';
import { cn } from '@veranda/shared/lib';
import type { ReactNode } from 'react';

export function CartLayout({
  data,
  children,
}: { data: CartProps; children: ReactNode }) {
  const { cart, open, setOpen, hasCartItems } = useCartSheet();
  useCartViewTracking({ domain: siteConfig.baseUrl });
  const cartItemCount = cart?.items?.length || 0;

  return (
    <CartShared.Root open={open} onOpenChange={setOpen}>
      <CartShared.Trigger>
        <NavbarButton
          icon={<CartIconWithCount count={cartItemCount} />}
          ariaLabel="Open cart"
        />
      </CartShared.Trigger>
      <CartShared.Content className="!z-[999999] [--color:var(--color-brand-burgundy)] sm:max-w-md">
        {children}
        <CartShared.Header className="mb-0">
          <CartShared.Title className="text-brand-burgundy">
            Your Cart ({cartItemCount})
          </CartShared.Title>
        </CartShared.Header>
        {hasCartItems && (
          <GWPCountdown discountId={data.promotion?.discountId} />
        )}
        <CartShared.Items>
          {hasCartItems ? (
            <>
              <CartShared.ItemsList>
                {cart?.items?.map((item, index) => (
                  <CartItem
                    key={item.id}
                    item={item}
                    baseUrl={siteConfig.baseUrl}
                    className={cn(index === 0 && 'pt-0')}
                  />
                ))}
              </CartShared.ItemsList>
            </>
          ) : (
            <CartShared.EmptyWrapper>
              <CartShared.EmptyHeading className="typography-outfit-h3 font-medium text-primary" />
              <CartEmptyItems
                className="font-medium"
                onClick={() => setOpen(false)}
                items={data.emptyCartItems?.map((item) => ({
                  _key: item._key,
                  title: item.link?.text || '',
                  slug: item.link?.slug || '',
                  image: item.image,
                }))}
              />
            </CartShared.EmptyWrapper>
          )}
          {hasCartItems && (
            <CartShared.Close>
              <Button variant="primary-outline" className="mt-6 w-full">
                continue shopping
              </Button>
            </CartShared.Close>
          )}
        </CartShared.Items>
        <CartShared.Footer className="p-6">
          {hasCartItems ? (
            <CartSummary />
          ) : (
            <CartShared.Close>
              <Button className="my-2 w-full" asChild>
                <CustomLink href={data.shopAllLink.slug || ''}>
                  Shop All
                </CustomLink>
              </Button>
            </CartShared.Close>
          )}
        </CartShared.Footer>
      </CartShared.Content>
    </CartShared.Root>
  );
}

function CartIconWithCount({ count }: { count: number }) {
  return (
    <div className="relative">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 506 506"
        fill="currentColor"
        className="size-6 text-primary"
        id="cart-button-icon"
      >
        <title>Cart</title>
        <g>
          <path
            d="M499.76,114.13c-1.04-1.28-2.61-2.02-4.26-2.02H140.26l-11.58-54.84c-0.35-1.66-1.45-3.06-2.97-3.81L17.31,0.56
		c-2.71-1.32-5.97-0.22-7.33,2.47l-9.4,18.7c-0.66,1.32-0.77,2.84-0.29,4.24s1.49,2.54,2.82,3.18l96.56,46.62l49.45,236.15
		c0.53,2.55,2.78,4.37,5.38,4.37h287.32c4.88,3.44,21.59,16.7,14.41,32.22c-4.56,6.43-10.68,12.88-21.33,12.88H147.3
		c-3.04,0-5.5,2.46-5.5,5.5v20.6c0,3.04,2.46,5.5,5.5,5.5H435c21.17,0,40.12-11.14,50.69-29.79c0.02-0.04,0.04-0.08,0.06-0.12
		c8.53-15.94,7.04-33.56-4.29-50.95c-5.77-8.84-12.66-15.43-16.41-18.68l35.84-174.76C501.22,117.09,500.81,115.41,499.76,114.13z
		 M176.75,284.4L146.8,143.8h316.95l-29.03,140.6H176.75z"
          />
          <path
            d="M206.7,411.1c-24.87,0-45.1,20.14-45.1,44.9s20.23,44.9,45.1,44.9s45.1-20.14,45.1-44.9S231.57,411.1,206.7,411.1z
		 M220,456c0,7.28-5.97,13.2-13.3,13.2s-13.3-5.92-13.3-13.2c0-7.4,5.84-13.2,13.3-13.2S220,448.6,220,456z"
          />
          <path
            d="M419.4,411.1c-24.87,0-45.1,20.14-45.1,44.9c0,24.81,20.23,45,45.1,45c12.62,0,24.81-5.21,33.44-14.28
		c8.06-8.47,12.19-19.39,11.66-30.76C464.43,431.21,444.22,411.1,419.4,411.1z M432.73,455.58c-0.02,0.17-0.03,0.35-0.03,0.53
		c0,7.4-5.84,13.2-13.3,13.2s-13.3-5.8-13.3-13.2s5.84-13.2,13.3-13.2c4,0,7.92,1.69,10.5,4.53
		C431.39,449.07,433.09,451.81,432.73,455.58z"
          />
        </g>
      </svg>
      <span className="-right-2 -top-2 absolute flex h-5 w-5 items-center justify-center rounded-full bg-brand-burgundy font-semibold text-brand-white text-xs leading-none">
        {count}
      </span>
    </div>
  );
}
